import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import ApiPath from "./../ConstantApi";
import { setAccessToken, logout } from "./../../Redux/Slices/AuthSlice";
import { showSnackbar } from "./../../Redux/Slices/NotificationSlice";
import { getDeviceId } from "../AuthService";

// Generate and store a unique device ID if not already present

let isRefreshing = false;
let failedQueue: any[] = [];
let lastErrorSnackbarTime = 0;

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

const RtkBaseQueryWithReauth = (baseUrl: string) => {
  return async (args: any, api: any, extraOptions: any) => {
    const baseQuery = fetchBaseQuery({
      baseUrl,
      prepareHeaders: (headers, { getState }) => {
        const {
          AuthSlice: { accessToken },
        }: any = getState();
        // Check if this is a file upload based on the body being FormData
        const isFileUpload = args.body instanceof FormData;

        if (!isFileUpload) {
          headers.set("Content-Type", "application/json");
        }

        if (accessToken) {
          headers.set("Authorization", `Bearer ${accessToken}`);
        }

        headers.set("x-device-id", getDeviceId());

        return headers;
      },
    });

    // First attempt at the request
    let result = await baseQuery(args, api, extraOptions);

    // Handle network/connection errors and unexpected server errors
    if (result?.error) {
      const now = Date.now();
      const shouldShowSnackbar = now - lastErrorSnackbarTime > 3000; // debounce: 3s

      if (shouldShowSnackbar) {
        const errorStatus = result.error.status;

        if (errorStatus === "FETCH_ERROR") {
          lastErrorSnackbarTime = now;
          api.dispatch(
            showSnackbar({
              message: "No internet connection. Please check your network.",
              severity: "error",
            })
          );
        } else if (errorStatus === "TIMEOUT_ERROR") {
          lastErrorSnackbarTime = now;
          api.dispatch(
            showSnackbar({
              message: "Request timed out. Please try again.",
              severity: "warning",
            })
          );
        } else if (errorStatus === 403) {
          // The API enforces per-module permissions server-side. A 403 means the
          // signed-in user genuinely lacks this permission -- it is NOT an auth
          // failure, so it must not fall through to the refresh/logout path
          // below, which would look like being logged out at random.
          lastErrorSnackbarTime = now;
          const data: any = (result.error as any).data;

          /*
           * Except when the ACCOUNT itself is finished.
           *
           * Deactivating an employee made the API refuse every request,
           * correctly -- and the person stayed on the page, watching a
           * toast, still apparently signed in. The comment above is right
           * that a 403 must not sign anyone out, but this is the other
           * kind of 403: not "you may not do this", but "you are no
           * longer anyone". The server marks it ACCOUNT_INACTIVE so the
           * two can be told apart.
           *
           * Signed out properly -- Redux, storage, and back to the login
           * page -- rather than left on a screen that cannot load.
           */
          if (data?.code === "ACCOUNT_INACTIVE") {
            api.dispatch(
              showSnackbar({
                message:
                  data?.message ||
                  "Your account is no longer active. Please contact an administrator.",
                severity: "error",
              })
            );
            api.dispatch(logout());
            try {
              localStorage.clear();
              sessionStorage.clear();
            } catch (storageError) {
              // A locked-down browser must not stop the redirect below.
            }
            // Hash routing, and a full reload rather than a route change:
            // every cached query belongs to an account that no longer
            // exists, so the app should start again from nothing.
            window.location.href = "/#/login";
            window.location.reload();
            return result;
          }

          api.dispatch(
            showSnackbar({
              message:
                data?.message ||
                "You do not have permission to perform this action.",
              severity: "warning",
            })
          );
        } else if (
          typeof errorStatus === "number" &&
          [500, 502, 503, 504].includes(errorStatus)
        ) {
          lastErrorSnackbarTime = now;
          api.dispatch(
            showSnackbar({
              message: "Something went wrong. Please try again later.",
              severity: "error",
            })
          );
        }
      }
    }

    // Check if we got a 401 error or if token is expired
    const {
      AuthSlice: {
        accessToken,
        accessTokenExpiry,
        refreshToken,
        isAuthenticated,
      },
    }: any = api.getState();
    const currentTime = Math.floor(Date.now() / 1000);

    const shouldRefresh =
      (result?.error?.status === 401 ||
        (accessToken &&
          accessTokenExpiry &&
          accessTokenExpiry <= currentTime)) &&
      isAuthenticated &&
      refreshToken;

    if (shouldRefresh) {
      if (isRefreshing) {
        // If already refreshing, wait for the current refresh to complete
        try {
          const token = await new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });

          // Retry with new token
          const newHeaders = new Headers(args.headers || {});
          newHeaders.set("Authorization", `Bearer ${token}`);
          return baseQuery({ ...args, headers: newHeaders }, api, extraOptions);
        } catch (err) {
          return { error: { status: 401, data: "Token refresh failed" } };
        }
      }

      isRefreshing = true;

      try {
        // Try refresh token request
        const refreshResult: any = await baseQuery(
          {
            url: ApiPath.refreshToken,
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              // Some servers might expect refresh token in Authorization header
              Authorization: `Bearer ${refreshToken}`,
            },
            body: { refreshToken },
          },
          api,
          extraOptions
        );

        // Check if refresh was successful
        if (refreshResult?.data?.accessToken) {
          const newAccessToken = refreshResult.data.accessToken;
          const newRefreshToken =
            refreshResult.data.refreshToken || refreshToken;

          // Update tokens in Redux store
          api.dispatch(
            setAccessToken({
              accessToken: newAccessToken,
              refreshToken: newRefreshToken,
            })
          );

          // Process queued requests
          processQueue(null, newAccessToken);

          // Retry the original request with new token
          const newHeaders = new Headers(args.headers || {});
          newHeaders.set("Authorization", `Bearer ${newAccessToken}`);

          return baseQuery({ ...args, headers: newHeaders }, api, extraOptions);
        } else if (refreshResult?.error) {
          // Handle specific refresh token errors
          const errorData = refreshResult.error.data;
          if (
            errorData?.message === "Invalid refresh token" ||
            refreshResult.error.status === 401
          ) {
            throw new Error("Refresh token expired");
          }
          throw new Error(
            `Refresh failed: ${errorData?.message || "Unknown error"}`
          );
        } else {
          throw new Error("No access token in refresh response");
        }
      } catch (error) {
        // Process failed queue
        processQueue(error, null);

        // Logout user
        api.dispatch(logout());

        // Clear storage
        try {
          localStorage.clear();
          sessionStorage.clear();
        } catch (storageError) {
        }

        // Redirect to login (you can uncomment this if needed)
        // window.location.href = '/login';

        return { error: { status: 401, data: "Authentication failed" } };
      } finally {
        isRefreshing = false;
      }
    }

    return result;
  };
};

export default RtkBaseQueryWithReauth;
